import { Entity } from '@api/api/entity-schema';
import { isScenarioAnalysisItem } from '@api/api/inventory-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { chunk, dedupeArray } from '@helpers/utils/arrays/arrays';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { yieldToAnimationFrame } from '@helpers/utils/dom/DOM';
import { QueryClient, useQueries } from '@tanstack/react-query';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { createEntityQueryOptions } from '@terminal/react-query-hooks/entities/useEntities.utils';
import { QUERY_KEY_INSIGHT } from '@terminal/react-query-hooks/insights/useInsight';
import { getInsight } from '@terminal/services/insights/insights-service';
import { normalizeFinancialStatementItems } from '../financial-statements-item/financialStatementUtils';
import { scenarioEntityTag } from '../scenario-item/formatScenarioDescription';
import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';
import { EMPTY_CONVERSATION_INVENTORY } from '../services/inventory-service';

const ENTITY_BATCH_SIZE = 20;

const getScenarioEntityTags = (
  inventory: ConversationInventory | undefined
): string[] =>
  dedupeArray(
    (inventory?.analysis?.items ?? [])
      .filter(isScenarioAnalysisItem)
      .map(i => i.entity ?? scenarioEntityTag(i.description))
      .filter((tag): tag is string => !!tag)
  );

const entityNamesByTag = (entities: Entity[]): Record<string, string> =>
  Object.fromEntries(entities.map(e => [e.tag, e.name_short || e.name_full]));

const resolveEntities = async (
  tags: string[],
  queryClient: QueryClient
): Promise<Entity[]> => {
  const batches = chunk(tags, ENTITY_BATCH_SIZE);
  const results: Entity[] = [];
  for (let i = 0; i < batches.length; i++) {
    const batchResults = await Promise.allSettled(
      batches[i].map(tag =>
        queryClient
          .query({
            ...createEntityQueryOptions(tag),
            staleTime: 'static',
          })
          .then(r => r?.data)
      )
    );
    results.push(
      ...batchResults
        .filter(
          (r): r is PromiseFulfilledResult<Entity | undefined> =>
            r.status === 'fulfilled'
        )
        .map(r => r.value)
        .filter((e): e is Entity => e !== undefined)
    );
    if (i < batches.length - 1) {
      await yieldToAnimationFrame();
    }
  }
  return results;
};

/**
 * Imperatively fetches and formats ALL inventory entities in batches, yielding
 * between each batch to keep the main thread responsive. Uses React Query's
 * queryClient.query so results are cached — subsequent calls are instant.
 */
export const fetchFormattedInventory = async (
  inventory: ConversationInventory | undefined,
  queryClient: QueryClient
): Promise<FormattedConversationInventory> => {
  if (!inventory) {
    return EMPTY_CONVERSATION_INVENTORY;
  }

  const insightIds = inventory.insight?.items?.map(item => item.id) ?? [];
  const entityTags = dedupeArray(
    inventory.entity?.items?.map(i => i.tag) ?? []
  );
  const snakes = inventory.timeseries?.items?.map(i => i.snake) ?? [];
  const snakesEntityTags = dedupeArray(snakes.map(s => s.split('.')[0]));
  const financialStatementItems = normalizeFinancialStatementItems(
    inventory['financial-statement']?.items ?? []
  );
  const financialStatementsEntityTags = dedupeArray(
    financialStatementItems.map(item => item.entityTag)
  );
  const calendarEarningsTags = dedupeArray(
    (inventory['economic-calendar']?.items ?? [])
      .map(i => i.entity_tag)
      .filter((tag): tag is string => !!tag)
  );
  const scenarioTags = getScenarioEntityTags(inventory);

  const [
    entitiesData,
    snakesData,
    financialStatementsData,
    calendarData,
    scenarioData,
    insightResults,
  ] = await Promise.all([
    resolveEntities(entityTags, queryClient),
    resolveEntities(snakesEntityTags, queryClient),
    resolveEntities(financialStatementsEntityTags, queryClient),
    resolveEntities(calendarEarningsTags, queryClient),
    resolveEntities(scenarioTags, queryClient),
    Promise.allSettled(
      insightIds.map(id =>
        queryClient.query({
          queryKey: [QUERY_KEY_INSIGHT, id],
          queryFn: () => getInsight(id),
          staleTime: 'static',
        })
      )
    ),
  ]);

  const insightTitles = Object.fromEntries(
    insightIds.map((id, i) => {
      const result = insightResults[i];
      const res = result?.status === 'fulfilled' ? result.value : undefined;
      const title = res?.data?.title ?? res?.data?.simple_title ?? id;
      return [id, title];
    })
  );
  const entityTickerByTag = Object.fromEntries(
    entitiesData.map(e => [e.tag, formatTicker(e)])
  );
  const snakeTickerByTag = Object.fromEntries(
    snakesData.map(e => [e.tag, formatTicker(e)])
  );
  const formattedTickerByEntityTag = Object.fromEntries(
    financialStatementsData.map(e => [e.tag, formatTicker(e)])
  );
  const economicCalendarEntityNames = entityNamesByTag(calendarData);
  const scenarioEntityNames = entityNamesByTag(scenarioData);

  const entityItems = entityTags.map(tag => ({
    tag: entityTickerByTag[tag] ?? tag,
  }));
  // Preserve the full snake identifier (prefix.suffix); only the entity prefix is formatted.
  const snakeItems = snakes.map(snake => {
    const [entityTag, ...rest] = snake.split('.');
    const formattedPrefix = snakeTickerByTag[entityTag] ?? entityTag;
    return {
      snake:
        rest.length > 0
          ? `${formattedPrefix}.${rest.join('.')}`
          : formattedPrefix,
    };
  });
  const formattedFinancialStatementItems = financialStatementItems.map(
    item => ({
      tag: formattedTickerByEntityTag[item.entityTag] ?? item.entityTag,
      statement: item.statement,
    })
  );

  return {
    ...inventory,
    ...(entityTags.length > 0 && { entity: { items: entityItems } }),
    ...(snakes.length > 0 && { timeseries: { items: snakeItems } }),
    ...(formattedFinancialStatementItems.length > 0 && {
      'financial-statement': { items: formattedFinancialStatementItems },
    }),
    ...(Object.keys(insightTitles).length > 0 && { insightTitles }),
    ...(Object.keys(economicCalendarEntityNames).length > 0 && {
      economicCalendarEntityNames,
    }),
    ...(Object.keys(scenarioEntityNames).length > 0 && { scenarioEntityNames }),
  };
};

export const useFormattedInventory = (
  inventory: ConversationInventory | undefined
): FormattedConversationInventory => {
  const insightIds = inventory?.insight?.items?.map(item => item.id) ?? [];

  const insightQueryResults = useQueries({
    queries: insightIds.map(id => ({
      queryKey: [QUERY_KEY_INSIGHT, id],
      queryFn: () => getInsight(id),
      enabled: insightIds.length > 0,
      staleTime: getTimeTillEndOfDayMs(),
    })),
  });

  const insightTitles = Object.fromEntries(
    insightIds.map((id, i) => {
      const res = insightQueryResults[i]?.data;
      const title = res?.data?.title ?? res?.data?.simple_title ?? id;
      return [id, title];
    })
  );

  const calendarEarningsTags = dedupeArray(
    (inventory?.['economic-calendar']?.items ?? [])
      .map(i => i.entity_tag)
      .filter((tag): tag is string => !!tag)
  );
  const { data: calendarEarningsData } = useEntities({
    entityTags: calendarEarningsTags,
  });
  const economicCalendarEntityNames = entityNamesByTag(calendarEarningsData);
  const scenarioTags = getScenarioEntityTags(inventory);
  const { data: scenarioData } = useEntities({ entityTags: scenarioTags });
  const scenarioEntityNames = entityNamesByTag(scenarioData);

  const entityTags = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const snakes = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const financialStatementItems = normalizeFinancialStatementItems(
    inventory?.['financial-statement']?.items ?? []
  );
  const financialStatementsEntityTags = dedupeArray(
    financialStatementItems.map(item => item.entityTag)
  );
  const snakesEntityTags = dedupeArray(
    snakes.map(snake => snake.split('.')[0])
  );
  const uniqueEntityTags = dedupeArray(entityTags);

  const { data: snakesData } = useEntities({ entityTags: snakesEntityTags });
  const { data: entitiesData } = useEntities({ entityTags: uniqueEntityTags });
  const { data: financialStatementsData } = useEntities({
    entityTags: financialStatementsEntityTags,
  });

  const entityTickerByTag = Object.fromEntries(
    entitiesData.map(e => [e.tag, formatTicker(e)])
  );
  const snakeTickerByTag = Object.fromEntries(
    snakesData.map(e => [e.tag, formatTicker(e)])
  );

  const entityItems = uniqueEntityTags.map(tag => ({
    tag: entityTickerByTag[tag] ?? tag,
  }));
  // Preserve the full snake identifier (prefix.suffix); only the entity prefix is formatted.
  const snakeItems = snakes.map(snake => {
    const [entityTag, ...rest] = snake.split('.');
    const formattedPrefix = snakeTickerByTag[entityTag] ?? entityTag;
    return {
      snake:
        rest.length > 0
          ? `${formattedPrefix}.${rest.join('.')}`
          : formattedPrefix,
    };
  });
  const formattedTickerByEntityTag = Object.fromEntries(
    financialStatementsData.map(entity => [entity.tag, formatTicker(entity)])
  );
  const formattedFinancialStatementItems = financialStatementItems.map(
    item => ({
      tag: formattedTickerByEntityTag[item.entityTag] ?? item.entityTag,
      statement: item.statement,
    })
  );

  return {
    ...inventory,
    ...(uniqueEntityTags.length > 0 && {
      entity: { items: entityItems },
    }),
    ...(snakes.length > 0 && { timeseries: { items: snakeItems } }),
    ...(formattedFinancialStatementItems.length > 0 && {
      'financial-statement': {
        items: formattedFinancialStatementItems,
      },
    }),
    ...(Object.keys(insightTitles).length > 0 && { insightTitles }),
    ...(Object.keys(economicCalendarEntityNames).length > 0 && {
      economicCalendarEntityNames,
    }),
    ...(Object.keys(scenarioEntityNames).length > 0 && { scenarioEntityNames }),
  };
};
